import * as React from "react";
import { useCallback, useState } from "react";
import { Box, Card, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { SimpleBreadcrumbs } from "../../components/navigation/simpleBreadcrumbs";
import StandardLayout from "../../layouts/standard";
import {
    useGetCclParticipantsByDateMutation,
    useLazyGetCclParticipantsByEmailQuery,
} from "../../services/cclTokenedEnterpriseParticipantApi";
import RegistrationsDataGridPro from "./registrationList";
import { useDispatch, useSelector } from "react-redux";
import {
    RegistrationListConfigState,
    updateRegistrationSearchParams,
} from "../../app/slices/registrationListConfigSlice";
import { getDateStringAdjusted, getUtcDateStringWithoutOffset } from "../../utilities/dateHelpers";
import CclSearchDefault from "../../components/common/cclSearchDefault";
import { ParticipantSearchResult } from "../../services/types/enterpriseParticipantApiTypes";
import CclTextOrDateSearchBar from "../../components/common/cclLandingPageSearchBars/cclTextOrDateSearchBar";
import { eGroupIds, UserClaimsService } from "../../services/currentUserService/currentUserService";
import ActiveRegistrationsTabPanel from "../resources/activeRegistrationsTabPanel";
import CclButton from "../../components/common/cclButtons/cclButton";
import RegistrationAdvancedSearchControl from "./advancedSearch/registrationSearchControl";
import {
    AdvancedSavedSearchItem,
    KeyValue,
    RegistrationSavedSearchItem,
    SessionSavedSearchItem,
} from "../../services/types/enterpriseCoachingApiTypes";

export const Registrations: React.FC = () => {
    // redux state
    const dispatch = useDispatch();
    const config = useSelector((state: any) => state.registrationListConfig);
    const claimsService = new UserClaimsService();
    const clientAdmin = claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);

    const [localConfig, setLocalConfig] = useState<RegistrationListConfigState>({ ...config });
    const [registrations, setRegistrations] = useState<ParticipantSearchResult[] | undefined>(
        undefined,
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const [runSearch] = useGetCclParticipantsByDateMutation();
    const [runEmailSearch] = useLazyGetCclParticipantsByEmailQuery();

    const [openAdvancedSearch, setOpenAdvancedSearch] = useState<boolean>(false);

    const [chips, setChips] = React.useState<KeyValue[]>([]);
    const [preSearchChips, setPreSearchChips] = React.useState<KeyValue[]>([]);
    const [baseLength, setBaseLength] = React.useState<number>(0);
    const [excessLength, setExcessLength] = React.useState<number>(0);
    const [showChips, setShowChips] = React.useState<boolean>(false);

    const executeSearch = useCallback(
        (searchConfig: RegistrationListConfigState) => {
            if (
                searchConfig.IsDateSearchActive &&
                searchConfig.StartDate !== "" &&
                searchConfig.EndDate !== ""
            ) {
                runSearch({
                    start: getUtcDateStringWithoutOffset(new Date(searchConfig.StartDate)),
                    end: getUtcDateStringWithoutOffset(new Date(searchConfig.EndDate)),
                })
                    .unwrap()
                    .then((data) => {
                        setIsLoading(false);
                        setRegistrations([...data]);
                    })
                    .catch((err) => {
                        setIsError(true);
                        setIsLoading(false);
                        setError(err.error.message);
                    });
            } else if (!searchConfig.IsDateSearchActive && searchConfig.EmailSearch !== "") {
                runEmailSearch(searchConfig.EmailSearch)
                    .unwrap()
                    .then((data) => {
                        setIsLoading(false);
                        setRegistrations([...data]);
                    })
                    .catch((err) => {
                        setIsError(true);
                        setIsLoading(false);
                        setError(err.error.message);
                    });
            }
            setLocalConfig({ ...searchConfig });
            clearChips();
            dispatch(updateRegistrationSearchParams(searchConfig));
        },
        [runSearch, runEmailSearch, dispatch],
    );

    React.useEffect(() => {
        executeSearch(localConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [executeSearch]);

    const getRegistrationsByDate = async (startDt: Date | null, endDt: Date | null) => {
        if (startDt == null || endDt == null) {
            return;
        }
        setIsLoading(true);
        setIsError(false);
        executeSearch({
            StartDate: startDt.toISOString(),
            EndDate: endDt.toISOString(),
            EmailSearch: "",
            IsDateSearchActive: true,
        });
    };

    const getRegistrationsByEmail = async (email?: string) => {
        if (email == null || email?.trim() === "") {
            return;
        }
        setIsLoading(true);
        setIsError(false);
        executeSearch({
            StartDate: getDateStringAdjusted(-2),
            EndDate: getDateStringAdjusted(2),
            EmailSearch: email.trim().toLowerCase(),
            IsDateSearchActive: false,
        });
    };

    const getRegistrations = async (
        email: string,
        startDate: Date | null,
        endDate: Date | null,
    ) => {
        if (email === "") {
            getRegistrationsByDate(startDate, endDate);
        } else {
            getRegistrationsByEmail(email);
        }
    };

    const sessionObj = {} as SessionSavedSearchItem;
    const registrationObj = {} as RegistrationSavedSearchItem;

    const [currentSearch, setCurrentSearch] = React.useState<AdvancedSavedSearchItem>({
        session: sessionObj,
        registration: registrationObj,
    });

    const updateCurrentSearch = (search: AdvancedSavedSearchItem) => {
        setCurrentSearch(search);
        let temp: any = { ...search.registration, ...search.session };
        temp = Object.values(temp);
        setPreSearchChips(temp);
        if (
            Object.keys(search.registration).length === 0 &&
            Object.keys(search.session).length === 0
        ) {
            clearChips();
            setRegistrations(undefined);
        }
    };

    const onAdvancedSearch = (participants: ParticipantSearchResult[]) => {
        let temp = preSearchChips;
        for (let i = 0; i < temp.length; i++) {
            if (!temp[i]) {
                temp.splice(i, 1);
                i--;
                continue;
            }
            if (temp[i].label === "" || temp[i].label === null || temp[i].label === undefined) {
                temp.splice(i, 1);
                i--;
            }
        }
        setChips(temp);
        if (temp.length > 4) {
            setExcessLength(temp.length - 4);
        } else {
            setExcessLength(0);
        }
        setBaseLength(temp.length);
        setShowChips(true);
        setRegistrations(participants);
    };

    const clearChips = () => {
        setChips([]);
        setPreSearchChips([]);
        setShowChips(false);
        setBaseLength(0);
        setExcessLength(0);
        const sessionObj = {} as SessionSavedSearchItem;
        const registrationObj = {} as RegistrationSavedSearchItem;
        setCurrentSearch({ session: sessionObj, registration: registrationObj });
    };

    const chipsDisplay = () => {
        if (!chips || chips.length === 0 || !showChips) return;
        let displayChips = chips.slice(0, 4);
        let excessChips = chips.slice(4);

        return (
            <>
                {displayChips.map((x) => {
                    if (!x || !x.label || x.label === "") return;
                    return <Chip key={x.id} label={x.label} sx={{ m: 1 }} />;
                })}
                {excessLength > 0 && (
                    <Tooltip
                        title={
                            <Box>
                                {excessChips.map((x) => {
                                    if (!x || !x.label || x.label === "") return;
                                    return (
                                        <Typography variant="body2" key={x.id} sx={{ m: 1 }}>
                                            {x.label}
                                        </Typography>
                                    );
                                })}
                            </Box>
                        }
                        arrow
                        placement="bottom"
                    >
                        <Typography sx={{ m: 1, alignSelf: "center" }}>+{excessLength}</Typography>
                    </Tooltip>
                )}
            </>
        );
    };

    const breadcrumbs = (
        <SimpleBreadcrumbs>
            <Typography variant="h6" color="text.primary">
                {clientAdmin ? "My Registrations" : "Registrations"}
            </Typography>
        </SimpleBreadcrumbs>
    );

    if (isError) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <span>{`Error Loading Registrations: ${error}`}</span>
            </StandardLayout>
        );
    }

    return (
        <React.Fragment>
            {openAdvancedSearch && (
                <RegistrationAdvancedSearchControl
                    open={openAdvancedSearch}
                    onClose={() => {
                        setOpenAdvancedSearch(false);
                    }}
                    onSearchResults={onAdvancedSearch}
                    updateCurrentSearch={updateCurrentSearch}
                    pastSearch={currentSearch}
                />
            )}
            <StandardLayout breadcrumbs={breadcrumbs}>
                {isError ? (
                    <span>{`Error Loading Registrations: ${error}`}</span>
                ) : clientAdmin ? (
                    <Stack height={1} width={1}>
                        <>
                            <Box
                                sx={{
                                    width: "100%",
                                    border: "1px solid #E0E0E0",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    display: "block",
                                    position: "relative",
                                }}
                            >
                                <Box
                                    component="img"
                                    src="https://coreprodeuscclstg.blob.core.windows.net/public-web-assets/access-assets/images/HomePageTitleBar.jpg"
                                    alt="Registrations Title Bar"
                                    sx={{
                                        width: "100%",
                                        height: "auto",
                                        display: "block",
                                    }}
                                />
                            </Box>
                            <Card
                                sx={{
                                    height: 1,
                                    width: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <ActiveRegistrationsTabPanel />
                            </Card>
                        </>
                    </Stack>
                ) : (
                    <Stack height={1} width={1}>
                        <Card
                            sx={{
                                height: 1,
                                width: 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Box sx={{ display: "flex", flexDirection: "row", width: 1, mb: 1 }}>
                                <Box sx={{ flex: "1", paddingRight: 1 }}>
                                    <CclTextOrDateSearchBar
                                        executeSearch={getRegistrations}
                                        initialTextFieldSearchTerm={localConfig.EmailSearch}
                                        textFieldSearchLabel="Enter Participant Email"
                                        initialStartDate={
                                            localConfig.IsDateSearchActive
                                                ? new Date(localConfig.StartDate)
                                                : null
                                        }
                                        initialEndDate={
                                            localConfig.IsDateSearchActive
                                                ? new Date(localConfig.EndDate)
                                                : null
                                        }
                                        startDateLabel="Earliest Start Date"
                                        endDateLabel="Latest Start Date"
                                        allowNullDates={false}
                                        showResetButton={true}
                                        clearSearchTrigger={undefined}
                                    />
                                </Box>
                            </Box>
                            {(claimsService.IsUserCclAdminOrSuperAdmin() || claimsService.IsUserNwaAdmin()) && (
                                <>
                                    <Divider />
                                    <Box
                                        sx={{
                                            width: "100%",
                                            m: 3,
                                            display: "flex",
                                            flexDirection: "row",
                                        }}
                                    >
                                        <CclButton
                                            onClick={() => {
                                                setOpenAdvancedSearch(true);
                                            }}
                                            mode={"secondary"}
                                            sx={{ mr: 3 }}
                                        >
                                            <Stack direction={"row"}>
                                                Advanced Search
                                                {baseLength > 0 && showChips && (
                                                    <Box
                                                        sx={{
                                                            ml: 2,
                                                            background: "#2F5F95",
                                                            borderRadius: "50%",
                                                            width: "20px",
                                                            color: "white",
                                                        }}
                                                    >
                                                        {baseLength}
                                                    </Box>
                                                )}
                                            </Stack>
                                        </CclButton>
                                        {chipsDisplay()}
                                    </Box>
                                    <Divider sx={{ mb: 4 }} />
                                </>
                            )}
                            {
                                /* this is our initial state where nothing is in the search bar. Should only happen before any search is done */
                                !localConfig.IsDateSearchActive &&
                                localConfig.EmailSearch === "" &&
                                registrations === undefined ? (
                                    <Box
                                        sx={{ position: "relative", width: "100%", height: "100%" }}
                                    >
                                        <CclSearchDefault
                                            line1="Start searching for Registrations"
                                            line2="Enter Participant Email or enter a Date Range and click Search or expand Advanced Search"
                                            backgroundImageUrl="https://coreprodeuscclstg.blob.core.windows.net/public-web-assets/access-assets/page-description-images/Registrations.jpg"
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            width: 1,
                                            height: 1,
                                            marginTop: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <RegistrationsDataGridPro
                                            loading={isLoading}
                                            registrations={registrations ?? []}
                                            showResultBar={false}
                                            resultBarText=""
                                        />
                                    </Box>
                                )
                            }
                        </Card>
                    </Stack>
                )}
            </StandardLayout>
        </React.Fragment>
    );
};

export default Registrations;
